/**
 * useSessionManager Hook
 * Provides session management functionality for React components
 */
import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import sessionManager from '../services/sessionManager';
import { logout } from '../features/auth/authSlice';

export const useSessionManager = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user, accessToken, refreshToken } = useSelector((state) => state.auth);

    const [sessionInfo, setSessionInfo] = useState(null);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [lastActivity, setLastActivity] = useState(Date.now());

    // Update session info
    const updateSessionInfo = useCallback(() => {
        const info = sessionManager.getSessionInfo();
        setSessionInfo(info);
    }, []);

    // Handle user activity
    const handleUserActivity = useCallback(() => {
        setLastActivity(Date.now());
        sessionManager.startSessionTimeout();
    }, []);

    // Handle session timeout
    const handleSessionTimeout = useCallback(() => {
        console.log('Session timeout detected');
        dispatch(logout());
        navigate('/login');
    }, [dispatch, navigate]);

    // Handle online status
    const handleOnlineStatus = useCallback(() => {
        setIsOnline(true);
        sessionManager.handleOnlineStatus();
    }, []);

    // Handle offline status
    const handleOfflineStatus = useCallback(() => {
        setIsOnline(false);
        sessionManager.handleOfflineStatus();
    }, []);

    // Handle visibility change
    const handleVisibilityChange = useCallback(() => {
        if (document.visibilityState === 'visible') {
            sessionManager.handleVisibilityChange();
        }
    }, []);

    // Handle window focus
    const handleWindowFocus = useCallback(() => {
        sessionManager.handleWindowFocus();
    }, []);

    // Setup event listeners
    useEffect(() => {
        // User activity listeners
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

        events.forEach(event => {
            document.addEventListener(event, handleUserActivity, true);
        });

        // Online/offline listeners
        window.addEventListener('online', handleOnlineStatus);
        window.addEventListener('offline', handleOfflineStatus);

        // Visibility change listener
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Window focus listener
        window.addEventListener('focus', handleWindowFocus);

        // Initial session info update
        updateSessionInfo();

        // Cleanup
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleUserActivity, true);
            });
            window.removeEventListener('online', handleOnlineStatus);
            window.removeEventListener('offline', handleOfflineStatus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [handleUserActivity, handleOnlineStatus, handleOfflineStatus, handleVisibilityChange, handleWindowFocus, updateSessionInfo]);

    // Update session info periodically
    useEffect(() => {
        const interval = setInterval(updateSessionInfo, 30000); // Every 30 seconds
        return () => clearInterval(interval);
    }, [updateSessionInfo]);

    // Handle session timeout - REMOVED to prevent conflicts
    // SessionManager already handles timeout, we don't need duplicate handler
    // useEffect(() => {
    //     if (sessionInfo?.isAuthenticated) {
    //         const timeout = setTimeout(() => {
    //             handleSessionTimeout();
    //         }, 24 * 60 * 60 * 1000); // 24 hours

    //         return () => clearTimeout(timeout);
    //     }
    // }, [lastActivity, sessionInfo?.isAuthenticated, handleSessionTimeout]);

    // Manual session refresh
    const refreshSession = useCallback(async() => {
        try {
            await sessionManager.refreshAccessToken();
            updateSessionInfo();
            return true;
        } catch (error) {
            console.error('Manual session refresh failed:', error);
            return false;
        }
    }, [updateSessionInfo]);

    // Clear session
    const clearSession = useCallback(() => {
        sessionManager.clearSession();
        dispatch(logout());
        navigate('/login');
    }, [dispatch, navigate]);

    // Get session status
    const getSessionStatus = useCallback(() => {
        if (!sessionInfo) return 'unknown';

        if (!sessionInfo.isAuthenticated) return 'unauthenticated';
        if (sessionInfo.isRefreshing) return 'refreshing';
        if (sessionInfo.needsRefresh) return 'needs_refresh';
        if (!isOnline) return 'offline';

        return 'active';
    }, [sessionInfo, isOnline]);

    // Get time until session expiry
    const getTimeUntilExpiry = useCallback(() => {
        if (!sessionInfo?.accessTokenExpiry) return null;

        const now = new Date();
        const expiry = new Date(sessionInfo.accessTokenExpiry);
        return expiry.getTime() - now.getTime();
    }, [sessionInfo]);

    // Get session warnings
    const getSessionWarnings = useCallback(() => {
        const warnings = [];

        if (!isOnline) {
            warnings.push('You are currently offline');
        }

        if (sessionInfo?.needsRefresh) {
            warnings.push('Session will refresh automatically');
        }

        const timeUntilExpiry = getTimeUntilExpiry();
        if (timeUntilExpiry && timeUntilExpiry < 5 * 60 * 1000) { // 5 minutes
            warnings.push('Session will expire soon');
        }

        return warnings;
    }, [sessionInfo, isOnline, getTimeUntilExpiry]);

    // Check if session is healthy
    const isSessionHealthy = useCallback(() => {
        return sessionInfo?.isAuthenticated &&
        !sessionInfo?.needsRefresh &&
        !sessionInfo?.isRefreshing &&
            isOnline;
    }, [sessionInfo, isOnline]);

    // Get session statistics
    const getSessionStats = useCallback(() => {
        return {
        isAuthenticated: sessionInfo?.isAuthenticated || false,
        isRefreshing: sessionInfo?.isRefreshing || false,
        needsRefresh: sessionInfo?.needsRefresh || false,
            isOnline,
            lastActivity: new Date(lastActivity),
            timeUntilExpiry: getTimeUntilExpiry(),
            warnings: getSessionWarnings(),
            status: getSessionStatus(),
            isHealthy: isSessionHealthy()
        };
    }, [sessionInfo, isOnline, lastActivity, getTimeUntilExpiry, getSessionWarnings, getSessionStatus, isSessionHealthy]);

    return {
        // Session info
        sessionInfo,
        isOnline,
        lastActivity,

        // Actions
        refreshSession,
        clearSession,
        updateSessionInfo,

        // Utilities
        getSessionStatus,
        getTimeUntilExpiry,
        getSessionWarnings,
        isSessionHealthy,
        getSessionStats,

        // Event handlers
        handleUserActivity,
        handleSessionTimeout,
        handleOnlineStatus,
        handleOfflineStatus,
        handleVisibilityChange,
        handleWindowFocus
    };
};

export default useSessionManager;